import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

export type ChoiceOption = { label: string; correct: boolean; response: string };

export type Fact = { label: string; title: string; value: string };

type CommonFields = {
  question: string;
  hint?: string;
  facts?: Fact[];
  goal?: string;
  math?: string;
};

export type ScaffoldStage =
  | (CommonFields & {
      kind: 'classify' | 'reflect';
      options: ChoiceOption[];
    })
  | (CommonFields & {
      kind: 'predict';
      expected: string | number;
      right_response: string;
      wrong_response: string;
    });

interface Props {
  stage: ScaffoldStage;
  wrongAttempts: number;
  onAdvance: () => void;
  onWrong: () => void;
  onReveal: (expectedAnswer: string) => void;
}

function normalize(v: string | number): string {
  return String(v).trim().toLowerCase().replace(/\s+/g, '');
}

// Cycle through palette for fact label chips.
const FACT_COLORS = [
  'bg-violet/20 text-violet',
  'bg-coral/15 text-coral',
  'bg-sun/30 text-ink',
  'bg-sky/15 text-sky',
  'bg-ink/10 text-ink',
];

function FactRow({ fact, colorIdx }: { fact: Fact; colorIdx: number }) {
  return (
    <div className="flex items-center gap-3 bg-white rounded-xl px-3 py-2 border border-ink/5">
      <span
        className={`shrink-0 w-7 h-7 rounded-lg font-display font-black text-sm flex items-center justify-center ${FACT_COLORS[colorIdx % FACT_COLORS.length]}`}
      >
        {fact.label}
      </span>
      <span className="flex-1 text-sm text-ink/80">{fact.title}</span>
      <span className="math-display font-bold text-ink">{fact.value}</span>
    </div>
  );
}

// Highlight the ? blank in display-math so it reads as "fill this in".
function renderMathBlock(math: string) {
  const parts = math.split(/(\?)/g);
  return (
    <div className="bg-white border border-ink/10 rounded-xl px-4 py-3 math-display text-center text-lg">
      {parts.map((p, i) =>
        p === '?' ? (
          <span key={i} className="text-coral font-bold">?</span>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </div>
  );
}

export default function ScaffoldStageView({ stage, wrongAttempts, onAdvance, onWrong, onReveal }: Props) {
  const [feedback, setFeedback] = useState<{ tone: 'good' | 'warm'; text: string } | null>(null);
  const [locked, setLocked] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFeedback(null);
    setLocked(false);
    setValue('');
    if (stage.kind === 'predict') {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [stage]);

  useEffect(() => {
    if (wrongAttempts >= 3 && !locked) {
      setLocked(true);
      if (stage.kind === 'predict') {
        const answer = String(stage.expected);
        setFeedback({
          tone: 'warm',
          text: `That's okay — the answer is ${answer}. Let's keep going together.`,
        });
        onReveal(answer);
        setTimeout(onAdvance, 1800);
      } else {
        const right = stage.options.find((o) => o.correct);
        setFeedback({ tone: 'warm', text: right?.response ?? "Let's move on." });
        setTimeout(onAdvance, 1400);
      }
    }
  }, [wrongAttempts, locked, stage, onAdvance, onReveal]);

  const pickChoice = (opt: ChoiceOption) => {
    if (locked) return;
    setFeedback({ tone: opt.correct ? 'good' : 'warm', text: opt.response });
    if (opt.correct) {
      setLocked(true);
      setTimeout(onAdvance, 1100);
    } else {
      onWrong();
    }
  };

  const checkPredict = () => {
    if (locked || stage.kind !== 'predict') return;
    if (!value.trim()) return;
    if (normalize(value) === normalize(stage.expected)) {
      setLocked(true);
      setFeedback({ tone: 'good', text: stage.right_response });
      setTimeout(onAdvance, 1300);
    } else {
      setFeedback({ tone: 'warm', text: stage.wrong_response });
      onWrong();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-5 space-y-3"
      key={stage.question}
    >
      {stage.facts && stage.facts.length > 0 && (
        <div className="space-y-2">
          {stage.facts.map((f, i) => (
            <FactRow key={i} fact={f} colorIdx={i} />
          ))}
        </div>
      )}

      {stage.goal && (
        <div className="bg-sun/30 text-ink rounded-xl px-4 py-2.5 text-sm font-bold leading-snug">
          {stage.goal}
        </div>
      )}

      {stage.math && renderMathBlock(stage.math)}

      {stage.question && (
        <div className="font-display font-bold text-base leading-snug">{stage.question}</div>
      )}

      {stage.hint && <div className="text-sm text-ink/60">{stage.hint}</div>}

      {stage.kind !== 'predict' && (
        <div className="flex flex-wrap gap-2">
          {stage.options.map((o, i) => (
            <button
              key={i}
              onClick={() => pickChoice(o)}
              disabled={locked}
              className="px-4 py-2.5 rounded-full border-2 border-ink/10 bg-white font-semibold text-sm hover:-translate-y-0.5 transition disabled:opacity-60"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}

      {stage.kind === 'predict' && (
        <div className="flex gap-2">
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') checkPredict();
            }}
            inputMode="text"
            placeholder="your guess"
            disabled={locked}
            className="flex-1 math-display text-lg px-4 py-3 rounded-2xl border-2 border-ink/10 bg-white outline-none focus:border-coral/60 disabled:opacity-60"
          />
          <button
            onClick={checkPredict}
            disabled={locked || !value.trim()}
            className="btn-primary disabled:opacity-50"
          >
            Check
          </button>
        </div>
      )}

      {feedback && (
        <div
          className={`rounded-xl px-4 py-3 text-sm font-semibold ${
            feedback.tone === 'good' ? 'bg-sky/10 text-sky' : 'bg-sun/20 text-ink'
          }`}
        >
          {feedback.text}
        </div>
      )}
    </motion.div>
  );
}
